import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authAPI, User } from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
  }) => Promise<boolean>;
  registerManufacturer: (manufacturerData: {
    companyName: string;
    businessLicense: string;
    contactEmail: string;
    contactPhone: string;
    contactPerson: string;
    industry: string;
    description?: string;
    website?: string;
  }) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isManufacturer: boolean;
  isConsumer: boolean;
  updateUser: (userData: Partial<User>) => void;
  refreshToken: () => Promise<boolean>;
  clearError: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const validateToken = async (token: string): Promise<boolean> => {
    try {
      // TODO: Replace with actual token validation API call
      const response = await fetch('/api/auth/validate', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  const clearAuthData = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setError(null);
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) return false;

    try {
      // TODO: Replace with actual refresh token API
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('authToken', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        return true;
      } else {
        // Refresh failed, logout user
        clearAuthData();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearAuthData();
      return false;
    }
  }, [clearAuthData]);

  // Initialize authentication on app load
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          
          // Validate token with server
          const isValid = await validateToken(token);
          if (isValid) {
            setUser(userData);
          } else {
            // Token invalid, clear storage
            clearAuthData();
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          clearAuthData();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [clearAuthData]);

  // Auto-refresh token before expiry - FIXED DEPENDENCY ARRAY
  useEffect(() => {
    if (user) {
      const refreshInterval = setInterval(async () => {
        await refreshToken();
      }, 15 * 60 * 1000); // Refresh every 15 minutes

      return () => clearInterval(refreshInterval);
    }
  }, [user, refreshToken]); // ✅ FIXED: Added refreshToken to dependency array

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authAPI.login({ email, password });
      const { user: userData, token, refreshToken } = response.data.data;

      // Store authentication data
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      setUser(userData);
      setIsLoading(false);
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      console.error('Login failed:', errorMessage);
      setIsLoading(false);
      return false;
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
  }): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authAPI.register({
        ...userData,
        role: userData.role || 'consumer' // Default to consumer
      });
      const { user: newUser, token, refreshToken } = response.data.data;

      // Store authentication data
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      setUser(newUser);
      setIsLoading(false);
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      console.error('Registration failed:', errorMessage);
      setIsLoading(false);
      return false;
    }
  };

  const registerManufacturer = async (manufacturerData: {
    companyName: string;
    businessLicense: string;
    contactEmail: string;
    contactPhone: string;
    contactPerson: string;
    industry: string;
    description?: string;
    website?: string;
  }): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual manufacturer registration API
      const response = await fetch('/api/manufacturers/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(manufacturerData)
      });

      if (response.ok) {
        const data = await response.json();
        
        // If registration includes immediate user creation
        if (data.user && data.token) {
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
        }
        
        setIsLoading(false);
        return true;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Manufacturer registration failed');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Manufacturer registration failed. Please try again.';
      setError(errorMessage);
      console.error('Manufacturer registration failed:', errorMessage);
      setIsLoading(false);
      return false;
    }
  };

  const updateUser = useCallback((userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }, [user]);

  const logout = useCallback(() => {
    // Optional: Notify server about logout
    const token = localStorage.getItem('authToken');
    if (token) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).catch(() => {
        // Ignore logout API errors
      });
    }
    
    clearAuthData();
  }, [clearAuthData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    register,
    registerManufacturer,
    logout,
    isAuthenticated: !!user,
    isManufacturer: user?.role === 'manufacturer',
    isConsumer: user?.role === 'consumer' || !user?.role, // Default to consumer
    updateUser,
    refreshToken,
    clearError,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Enhanced User type for ScanOnce 2.0
export interface EnhancedUser extends User {
  role: 'consumer' | 'manufacturer';
  manufacturerId?: string;
  companyName?: string;
  businessLicense?: string;
  industry?: string;
  verificationStatus?: 'pending' | 'verified' | 'rejected';
  permissions?: string[];
  lastLogin?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Utility hooks for specific user types
export const useManufacturer = () => {
  const { user, isManufacturer } = useAuth();
  return {
    manufacturer: isManufacturer ? user as EnhancedUser : null,
    isManufacturer
  };
};

export const useConsumer = () => {
  const { user, isConsumer } = useAuth();
  return {
    consumer: isConsumer ? user as EnhancedUser : null,
    isConsumer
  };
};

// Higher-order component for role-based access
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRole?: 'consumer' | 'manufacturer'
) => {
  return (props: P) => {
    const { isAuthenticated, user, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: '#64748b', margin: 0 }}>Loading...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      window.location.href = '/login';
      return null;
    }

    if (requiredRole && user?.role !== requiredRole) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Access Denied</h2>
            <p style={{ color: '#64748b' }}>
              You don't have permission to access this page.
            </p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};

export default AuthProvider;
